<template>
  <main class="py-12 space-y-12">
    <!-- Gastversion-Einstieg: Niederschwellige Call-to-Action -->
    <section id="guest-version-teaser" class="relative pb-16 lg:pb-24">
      <div class="container">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <!-- Text Content -->
          <div class="space-y-6">
            <h2 class="text-3xl lg:text-4xl font-bold text-primary">
              Testen Sie jetzt kostenlos!
            </h2>
            <p class="text-lg text-gray-700">
              Planen Sie Ihren Spielplatz sofort und unkompliziert. Ohne Registrierung, ohne versteckte Kosten.
              Versuchen Sie unseren innovativen Spielplatz-Konfigurator in der Gastversion —
              erleben Sie live, wie einfach Spielplatzplanung sein kann!
            </p>
            <ul class="space-y-3 text-base text-gray-600">
              <li class="flex items-center gap-3">
                <UIcon class="h-5 w-5 text-secondary" name="lucide:check-circle-2"/>
                <span>Kostenlos und ohne Anmeldung</span>
              </li>
              <li class="flex items-center gap-3">
                <UIcon class="h-5 w-5 text-secondary" name="lucide:check-circle-2"/>
                <span>3D-Visualisierung in Echtzeit</span>
              </li>
              <li class="flex items-center gap-3">
                <UIcon class="h-5 w-5 text-secondary" name="lucide:check-circle-2"/>
                <span>Alle Normen & Sicherheitsstandards automatisch berücksichtigt</span>
              </li>
            </ul>
            <div class="pt-4">
              <UButton
                  color="primary"
                  href="https://www.spielplatz-konfigurator.de/mein-neuer-spielplatz"
                  icon="lucide:play-circle"
                  size="xl"
                  target="_blank"
                  variant="solid"
              >
                Ihr Spielplatz – Jetzt konfigurieren!
              </UButton>
            </div>
          </div>

          <!-- Visual Teaser -->
          <div class="hidden lg:block">
            <video
                aria-label="Spielplatz-Konfigurator in Aktion"
                autoplay
                class="w-full h-auto rounded-lg shadow-lg"
                loop
                muted
                playsinline
                preload="metadata"
            >
              <source src="/videos/screencast_playground_configurator_web_optimized.mp4" type="video/mp4">
            </video>
          </div>
        </div>
      </div>
    </section>

    <section>
      <div class="container relative px-4">
        <div class="timeline space-y-16">
          <article class="timeline-item js-fade-in is-added is-visible">
            <div class="grid grid-cols-1 lg:grid-cols-4 gap-32">
              <HexagonImage img-src="AdobeStock_335015363.jpg"/>
              <div class="flex items-center justify-center">
                <NuxtPicture
                    alt=""
                    class="item-icon iconsize-small relative z-10 bg-background p-4 w-10/12"
                    src="/icons/Icons_10.png"
                />
              </div>
              <div class="lg:col-span-2">
                <h2 class="item-title">
                  Rufen Sie uns an oder schreiben Sie uns eine Mail
                </h2>
                <div
                    class="item-text"
                    style="padding-left:2px"
                >
                  <p>
                    Bei Sauerland Spielgeräte haben Sie zu jedem Zeitpunkt einen persönlichen
                    Ansprechpartner. Dazu müssen Sie nicht mehr tun, als uns eine Nachricht im
                    Kontaktfeld, über das Feld in der Ansprechpartnersuche oder per Anruf zu
                    kontaktieren.&nbsp;
                  </p>
                  <UButton
                      :to="{name: 'contact'}"
                      label="Kontakt aufnehmen"
                  />
                </div>
              </div>
            </div>
          </article>

          <article class="timeline-item js-fade-in is-added is-visible">
            <div class="grid grid-cols-1 lg:grid-cols-4 gap-32">
              <HexagonImage img-src="AdobeStock_118448109.jpg"/>
              <div class="flex items-center justify-center">
                <NuxtPicture
                    alt=""
                    class="item-icon iconsize-small relative z-10 bg-background p-4 w-10/12"
                    src="icons/Icons_11.png"
                />
              </div>
              <div class="lg:col-span-2">
                <h2 class="item-title">
                  Vereinbaren Sie einen persönlichen Beratungstermin
                </h2>
                <div
                    class="item-text"
                    style="padding-left:2px"
                >
                  <p>
                    Nach der Kontaktaufnahme vereinbaren Sie mit Ihrem persönlichen
                    Ansprechpartner
                    einen Beratungstermin, um ihren persönlichen Spielplatz zu konfigurieren.
                  </p>
                  <UButton
                      :to="{name: 'contact'}"
                      label="Jetzt Beratungstermin vereinbaren"
                  />
                </div>
              </div>
            </div>
          </article>

          <article class="timeline-item js-fade-in is-added is-visible">
            <div class="grid grid-cols-1 lg:grid-cols-4 gap-32">
              <HexagonImage img-src="AdobeStock_342278803.jpg"/>
              <div class="flex items-center justify-center">
                <NuxtPicture
                    alt=""
                    class="item-icon iconsize-small relative z-10 bg-background p-4 w-1/2"
                    src="icons/Icons_5.png"
                />
              </div>
              <div class="lg:col-span-2">
                <h2 class="item-title">
                  Planen Sie mit uns Ihren individuellen Spielplatz
                </h2>
                <div
                    class="item-text"
                    style="padding-left:2px"
                >
                  <p>
                    Unter Berücksichtigung Ihrer Angaben und aller Normen können Sie live Ihren
                    Spielplatz nach belieben konfigurieren lassen. Dabei bleibt kein Wunsch
                    offen!
                    Als Ergebnis erhalten Sie einen 3D visualisierten Projektvorschlag mit
                    umfangreichen Zeichnungen und Bildern, in welchen die Bewegungsabläufe, der
                    Spielwert und die Funktion Ihres Spielplatzes einfach erkennbar sind.
                  </p>
                  <UButton
                      :to="{name: 'contact'}"
                      label="Planungsgespräch vereinbaren"
                  />
                </div>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section>
      <div class="container">
        <h2>Gemeinsam planen und am Ende das Ergebnis in AR am eigenen Spielplatz live erleben.</h2>
        <p class="article-teaser">
          Von der 3D-Planung im Konfigurator, über die Sauerland Spielgeräte 3D-Planer-App, bis zur fertigen
          Spielkombination auf Ihrem Spielplatz. Sauerland Spielgeräte, Ihr Ansprechpartner für echte
          Live-Erlebnis-Beratung!
        </p>
      </div>
    </section>

    <section>
      <div class="container">
        <NuxtPicture
            :img-attrs="{ class: 'rounded-lg' }"
            alt="Sauerland-Spielgeräte_Erlebnisberatung-2023"
            src="Sauerland-Spielgeraete_Erlebnisberatung-2023.jpg"
        />
      </div>
    </section>

    <section>
      <div
          id="video-193"
          class="container grid grid-cols-1 lg:grid-cols-4 gap-8"
      >
        <div
            class="p-10 bg-secondary text-white hidden lg:block"
        >
          <NuxtPicture
              alt="Schauen Sie sich alternativ das Video auf Ihrem mobilen Gerät an!"
              class="mx-auto"
              src="/videos/Sauerland-Spielgeraete_QR-Code_Einleitung.jpg"
          />
          <span class="inline-block py-10 font-bold text-2xl uppercase">Schauen Sie sich alternativ das Video auf Ihrem mobilen Gerät an!</span>
        </div>
        <div class="lg:col-span-3">
          <VideoPlayer
              name="Sauerland-Spielgeraete_Film-1"
              poster="/videos/Einleitung.jpg"
              url="/videos/Sauerland-Spielgeraete_Film-1.mp4"
          />
        </div>
      </div>
    </section>

    <section
        class="relative bg-cover bg-center dp-contentblock block-title py-largest dp-fsimg"
        data-was-processed="true"
        style="background-image: url(/images/backgrounds/AdobeStock_236942594.jpg);"
    >
      <span class="noprint"/>
      <div class="relative z-10 py-48 dp-pagebounds">
        <h2 class="my-none py-large py-l-largest text-center text-white! js-fade-in is-added is-visible">
          Nehmen Sie noch heute Kontakt auf!<br>
          Tel.: +49 (0) 2955 76060<br>
        </h2>
        <div class="text-center mt-8">
          <UButton
              color="secondary"
              icon="i-heroicons-envelope-20-solid"
              size="xl"
              to="mailto:info@sauerland-spielgeraete.de"
              variant="solid"
          >
            info@sauerland-spielgeraete.de
          </UButton>
        </div>
      </div>
      <div class="absolute top-0 left-0 w-full z-10 pointer-events-none">
        <BrandWave
            :height="150"
            flip
            variant="trio"
        />
      </div>
      <span class="brand-overlay is-duo is-reversed is-vertical noprint"/>
      <div class="absolute bottom-0 left-0 w-full z-10 pointer-events-none">
        <BrandWave
            :height="150"
            variant="trio"
        />
      </div>
    </section>

    <SectionsSectionIntro
        :cta="{ label: 'Kontakt aufnehmen', to: {name: 'contact'}, trailingIcon: 'i-lucide-chevron-right' }"
        :images="sectionIntro.images"
        :teaser="sectionIntro.teaser"
        :text="sectionIntro.text"
        :title="sectionIntro.title"
    />

    <section>
      <div
          id="video-188"
          class="container grid grid-cols-1 lg:grid-cols-4 gap-8"
      >
        <div
            class="p-10 bg-secondary text-white hidden lg:block"
        >
          <NuxtPicture
              alt="Schauen Sie sich alternativ das Video auf Ihrem mobilen Gerät an!"
              class="mx-auto"
              src="/videos/Sauerland-Spielgeraete_QR-Code_Technische_Vorstellung.jpg"
          />
          <span class="inline-block py-10 font-bold text-2xl uppercase">Schauen Sie sich alternativ das Video auf Ihrem mobilen Gerät an!</span>
        </div>
        <div class="lg:col-span-3">
          <VideoPlayer
              name="Sauerland-Spielgeraete_Film-2"
              poster="/videos/Technische-Vorstellung.jpg"
              url="/videos/Sauerland-Spielgeraete_Film-2.mp4"
          />
        </div>
      </div>
    </section>

    <section>
      <div class="container">
        <h2 class="">
          Häufig gestellte Fragen zum Spielplatz-Konfigurator
        </h2>
        <p class="article-teaser">
          Einen Spielplatz zu planen ist nicht immer so leicht, wie man sich das vorstellt. Oft bleiben viele
          Fragen offen, die wir Ihnen in unserem FAQ gern beantworten möchten.
        </p>
        <p>
          Sollten Sie dennoch einige offene Fragen zu unserem Konfigurator haben, können Sie sich jederzeit
          bei Ihrem persönlichen Ansprechpartner melden. Dieser steht mit Rat und Tat an Ihrer Seite. Also
          nichts wie los!
        </p>
        <UButton
            :to="{name: 'contact'}"
            label="Kontakt aufnehmen"
        />
      </div>
    </section>

    <section>
      <UAccordion
          :default-value="['0']"
          :items="items"
          :ui="{
                  trigger: 'text-base',
                  body: 'text-base text-muted'
                }"
          :unmount-on-hide="false"
          class="container accordion"
      >
        <template #body="{ item }">
          <MDC
              :value="item.content"
          />
        </template>
      </UAccordion>
    </section>

    <section
        class="relative bg-cover bg-center"
        style="background-image: url(/images/backgrounds/BQ4C1710.jpg);"
    >
      <span class="brand-overlay is-amber noprint"/>
      <div class="relative z-10 py-48 dp-pagebounds">
        <h2 class="text-center text-white!">
          Nehmen Sie noch heute Kontakt mit uns auf und planen Sie<br>
          Ihren eigenen Spielplatz mit dem Sauerland Spielgeräte<br>
          Spielplatz-Konfigurator!
        </h2>
      </div>
      <div class="absolute top-0 left-0 w-full z-10 pointer-events-none">
        <BrandWave
            :height="150"
            flip
            variant="trio"
        />
      </div>
      <div class="absolute bottom-0 left-0 w-full z-10 pointer-events-none">
        <BrandWave
            :height="150"
            variant="trio"
        />
      </div>
    </section>

    <section>
      <div class="container">
        <h2>
          Kontaktformular
        </h2>
        <ContactForm/>
      </div>
    </section>
  </main>
</template>

<script setup lang="ts">
import {ref} from 'vue';
import type {AccordionItem} from '@nuxt/ui';
import HexagonImage from '~/components/HexagonImage.vue';
import ContactForm from '~/components/ContactForm.vue';
import VideoPlayer from '~/components/VideoPlayer.vue';

definePageMeta({
  path: '/Spielplatz-Konfigurator',
});

const header = useState<{ title: string; subTitle: string }>(
    'header',
);
header.value = {
  title: 'Der Sauerland Spielgeräte',
  subTitle: 'Spielplatz-Konfigurator',
};

const items = ref<AccordionItem[]>([
  {
    label: 'Wie gestalte ich einen Spielplatz?',
    content: '<p>Gerade während der verschiedenen und sich verändernden Einschränkungen während der Corona-Zeit, bieten wir Ihnen die Möglichkeit, egal von welchem PC oder Laptop, live an Ihrer Spielplatzplanung teil zu nehmen. Sie können dadurch direkt Einfluss auf die Planung nehmen und verschiedene Varianten durchspielen. Dazu benötigen Sie lediglich einen Internetzugang. Dies funktioniert ohne jeglichen Softwaredownload.</p>'
        + '<p>Treten Sie mit uns in Kontakt, um zu erfahren, wie kinderleicht heute eine Spielplatzplanung sein kann!</p>'
        + '<p><a class="button bordered" href="/Service/Kontakt/">Kontakt aufnehmen</a></p>',
  },
  {
    label: 'Was benötige ich?',
    content: '<p>Alles was Sie für unseren Spielplatz-Konfigurator benötigen, ist ein „ganz gewöhnlicher“ Computer oder einen Laptop, sowie etwas Fantasie und Kreativität.</p>'
        + '<p>Mit Ihrem persönlichen Ansprechpartner können Sie sofort einen Termin vereinbaren, um Ihren Spielplatz zu konfigurieren. Alles Weitere bespricht dieser mit Ihnen.</p>',
  },
  {
    label: 'Welche Unterstützung bekomme ich?',
    content: '<p>Nach Eingabe der Maße des Spielplatzes, können Sie sofort sehen ob die erforderlichen Sicherheitsabstände und Normen eingehalten werden können.</p>'
        + '<p>Nachdem Sie Ihre gewünschte Spielgerät-Kombination konfiguriert haben, können Sie mit einem Knopfdruck alle relevanten Fallhöhen, sowie die Platzgröße einsehen. Für alle weiteren Fragen steht Ihnen ihr Ansprechpartner zur Seite.</p>',
  },
  {
    label: 'Wer hilft mir?',
    content: '<p>Jeder Spielplatz ist individuell! Aus diesem Grund planen wir mit Ihnen zusammen Ihren ganz eigenen Spielplatz und begleiten Sie von Anfang bis Ende.</p>'
        + '<p>Dabei steht Ihnen jederzeit Ihr persönlicher Ansprechpartner mit Rat und Tat zur Seite. Diesen können Sie ganz einfach ermitteln, indem Sie im rechten Bildrand auf den grünen Button „Ansprechpartner suchen“ klicken. Dabei wird Ihre Postleitzahl abgefragt und im Handumdrehen ihr Ansprechpartner, sowie dessen Vertretung angezeigt.</p>'
        + '<p>Also worauf warten Sie? Konfigurieren Sie noch heute Ihren Spielplatz!</p>'
        + '<a class="button bordered" href="/Service/Kontakt">Kontakt aufnehmen</a>',
  },
  {
    label: 'Was kann ich alles konfigurieren?',
    content: '<p>Mit dem Spielplatz-Konfigurator haben Sie die Möglichkeit neben der einzelnen Spielgerät-Kombination, gleich den gesamten Spielplatz zu konfigurieren.</p>'
        + '<p>Dabei werden nicht nur optische Aspekte Ihres Spielplatzes beachtet, sondern alle relevanten Grundlagen und Normen direkt mit in Betracht gezogen. Ihre Spielgerät-Kombination und auch Ihr Spielplatz werden Maßstab-getreu im Konfigurator dargestellt.</p>',
  },
  {
    label: 'Welche Informationen erhalte ich zu meiner Konfiguration?',
    content: '<p>Mit dem Spielplatz-Konfigurator von Sauerland Spielgeräte erhalten Sie neben einer ersten Kostenkalkulation, eine Stückliste aller eingesetzten Spielmodule, technische Zeichnungen, Fotos und weitere Details als PDF. Dieses können Sie idealerweise für Präsentationen weiterverwenden.</p>'
        + '<p>Somit sparen Sie nicht nur viel Zeit bei der Anschaffung aller erforderlichen Informationen, sondern können spielend leicht Ihren Spielplatz planen!</p>',
  },
]);

const sectionIntro = {
  images: ['helm.png', 'sicherheit.png', 'zahnrad.png'],
  title: 'Eine neue Dimension - digitale Spielplatzplanung',
  teaser: 'Wenn aus einer ersten Idee eine Vision entsteht und aus einer Vision ein einzigartiges Erlebnis bei der Planung Ihres Spielplatzes, dann mit dem bahnbrechenden „Spielplatz-Konfigurator“ von Sauerland Spielgeräte!',
  text: '<p>Sauerland Spielgeräte zählt wegen der vielen innovativen und durchdachten Produkte seit vielen Jahren zu den führenden Herstellern von Spielgeräten. Im Fokus steht der Kundenservice, die Beratung und dass Sie sich als Kunde einfach wohlfühlen.</p>'
      + '<p>Vereinbaren Sie noch heute einen Termin um Ihren persönlichen Spielplatz zu konfigurieren!</p>',
};
</script>

<style scoped>
@media (min-width: 992px) {
  .timeline::after {
    background: var(--color-amber-600) /* amber-600 #b87a2a */;
    content: '';
    height: 100%;
    left: 35%;
    position: absolute;
    top: 0;
    width: 2px;
    z-index: 1;
  }
}
</style>
